import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import type { ActivityKind, Member, MemberScore } from '@pacer/shared';
import { levelForPoints } from '@pacer/shared';
import * as api from '../api';
import { Icon, type IconName } from './Icon';
import { LevelBadge } from './LevelBadge';
import { Modal } from './Modal';
import { Badge, cx } from './ui';
import { queryKeys } from '../state/queries';

const KIND_LABEL: Record<ActivityKind, string> = { chore: 'Chores', todo: 'To-dos', shopping: 'Shopping', plan: 'Plans' };
const KIND_ICON: Record<ActivityKind, IconName> = { chore: 'chores', todo: 'note', shopping: 'cart', plan: 'star' };

/**
 * The family scoreboard: everyone ranked by lifetime points, each with their level ring and
 * this-week tally. It's the reason to actually tick things off. Tap a person for the detail.
 */
export function Scoreboard() {
  const [detail, setDetail] = useState<Member | null>(null);
  const { data: scores = [] } = useQuery({ queryKey: queryKeys.scores, queryFn: api.getMemberScores });
  const { data: members = [] } = useQuery({ queryKey: queryKeys.members, queryFn: api.getMembers });

  const scoreOf = (id: string): MemberScore =>
    scores.find((s) => s.memberId === id) ?? {
      memberId: id,
      lifetime: 0,
      week: 0,
      month: 0,
      count: 0,
      byKind: { chore: 0, todo: 0, shopping: 0, plan: 0 },
    };

  const ranked = [...members].sort((a, b) => scoreOf(b.id).lifetime - scoreOf(a.id).lifetime);
  if (members.length === 0) return null;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2 border-b border-border-subtle pb-3">
        <Icon name="star" size={16} className="text-muted" />
        <h2 className="font-display text-[20px] text-primary">Scoreboard</h2>
      </div>

      <div className="flex flex-col gap-2">
        {ranked.map((m, i) => {
          const s = scoreOf(m.id);
          const { level, prestige, pct, into, span } = levelForPoints(s.lifetime);
          return (
            <button
              key={m.id}
              onClick={() => setDetail(m)}
              className="group flex items-center gap-3 rounded-lg border border-border-subtle bg-surface px-3 py-2.5 text-left transition-colors hover:border-border hover:bg-surface-high/60"
            >
              <span className="tabular w-4 shrink-0 text-center font-mono text-[12px] text-muted">{i + 1}</span>
              <LevelBadge name={m.name} color={m.color} photoUrl={m.avatarUrl} points={s.lifetime} size={44} />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="truncate text-[15px] text-primary">{m.name}</span>
                  <span className="mono font-mono text-[12px] text-muted">Lv {level}</span>
                  {prestige > 0 && (
                    <span className="flex shrink-0 items-center gap-0.5 font-mono text-[12px]" style={{ color: m.color }}>
                      <Icon name="star" size={11} strokeWidth={2} />
                      {prestige}
                    </span>
                  )}
                  {s.week > 0 && <span className="ml-auto shrink-0 font-mono text-[12px] text-accent">+{s.week} this week</span>}
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-border-subtle">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct * 100}%`, background: m.color }} />
                  </div>
                  <span className="tabular shrink-0 font-mono text-[11px] text-muted">
                    {into}/{span}
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {detail && <MemberStats member={detail} score={scoreOf(detail.id)} onClose={() => setDetail(null)} />}
    </section>
  );
}

function MemberStats({ member, score, onClose }: { member: Member; score: MemberScore; onClose: () => void }) {
  const { level, prestige, into, span, atMax } = levelForPoints(score.lifetime);
  const toNext = Math.max(0, span - into);
  const { data: feed = [] } = useQuery({
    queryKey: [...queryKeys.activity, member.id],
    queryFn: () => api.getActivity(member.id, 30),
  });

  const kinds: ActivityKind[] = ['chore', 'todo', 'shopping', 'plan'];

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow={prestige > 0 ? `Prestige ${prestige} · Level ${level}` : `Level ${level}`}
      title={member.name}
      size="lg"
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-4 rounded-lg border border-border-subtle bg-surface-high/50 p-4">
          <LevelBadge name={member.name} color={member.color} photoUrl={member.avatarUrl} points={score.lifetime} size={64} />
          <div className="min-w-0 flex-1">
            <div className="tabular font-mono text-[24px] leading-none font-medium text-primary">
              {score.lifetime} <span className="text-[13px] text-muted">pts</span>
            </div>
            <div className="mt-1.5 flex items-center gap-2">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-border-subtle">
                <div className="h-full rounded-full" style={{ width: `${span > 0 ? (into / span) * 100 : 0}%`, background: member.color }} />
              </div>
            </div>
            <div className="mt-1 font-mono text-[12px] text-muted">
              {toNext} to {atMax ? `prestige ${prestige + 1}` : `level ${level + 1}`} · {score.count} done all-time
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {kinds.map((k) => (
            <div key={k} className="flex flex-col items-center gap-1 rounded-md border border-border-subtle bg-surface px-2 py-3">
              <Icon name={KIND_ICON[k]} size={16} className="text-muted" />
              <span className="tabular font-mono text-[16px] text-primary">{score.byKind[k]}</span>
              <span className="eyebrow">{KIND_LABEL[k]}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          <span className="eyebrow">Recent</span>
          {feed.length === 0 ? (
            <p className="py-4 text-center font-mono text-[12px] text-muted">Nothing yet — start ticking things off.</p>
          ) : (
            <ul className="flex flex-col overflow-hidden rounded-md border border-border-subtle">
              {feed.map((a) => (
                <li key={a.id} className="flex items-center gap-3 border-b border-border-subtle px-3 py-2 last:border-b-0">
                  <Icon name={KIND_ICON[a.kind]} size={14} className={cx('shrink-0 text-muted')} />
                  <span className="min-w-0 flex-1 truncate text-[13px] text-primary">{a.label}</span>
                  <span className="tabular shrink-0 font-mono text-[12px] text-muted">
                    {DateTime.fromISO(a.createdAt).toRelative({ style: 'short' })}
                  </span>
                  <Badge tone="accent">+{a.points}</Badge>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
